import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, RefreshCw } from 'lucide-react';

type ProviderType = 'openai_compatible' | 'azure_openai';

interface LLMConfig {
  provider: string;
  provider_type?: ProviderType;
  base_url: string;
  api_key: string;
  model_name: string;
}

interface LLMConfigFormProps {
  title: string;
  description: string;
  initialConfig: LLMConfig;
  onSave: (config: LLMConfig) => Promise<void>;
  onTest: (config: LLMConfig) => Promise<void>;
}

const PROVIDER_TYPE_OPTIONS: { value: ProviderType; label: string }[] = [
  { value: 'openai_compatible', label: 'OpenAI Compatible' },
  { value: 'azure_openai', label: 'Azure OpenAI (via CF AI Gateway)' },
];

export const LLMConfigForm: React.FC<LLMConfigFormProps> = ({
  title,
  description,
  initialConfig,
  onSave,
  onTest,
}) => {
  const [config, setConfig] = useState<LLMConfig>({
    ...initialConfig,
    provider_type: initialConfig.provider_type || 'openai_compatible',
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 同步父组件传入的新 initialConfig（保存后 refetch 时触发）
  useEffect(() => {
    setConfig({
      ...initialConfig,
      provider_type: initialConfig.provider_type || 'openai_compatible',
    });
  }, [initialConfig]);

  const isAzure = config.provider_type === 'azure_openai';

  const handleInputChange = (field: keyof LLMConfig, value: string) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setMessage(null);
  };

  const handleSave = async () => {
    if (!isAzure && (!config.provider || !config.model_name)) {
      setMessage({ type: 'error', text: '所有字段都是必填的' });
      return;
    }
    if (!config.base_url || !config.api_key) {
      setMessage({ type: 'error', text: '所有字段都是必填的' });
      return;
    }

    if (config.api_key.includes('...')) {
      setMessage({ type: 'error', text: '请输入完整的 API Key，不能保存掩码格式' });
      return;
    }

    setIsSaving(true);
    setMessage(null);

    // Azure 模式下自动填充隐藏字段的默认值
    const saveConfig = isAzure
      ? { ...config, provider: config.provider || 'Azure OpenAI', model_name: config.model_name || 'azure' }
      : config;

    try {
      await onSave(saveConfig);
      setMessage({ type: 'success', text: '配置保存成功' });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || '保存失败',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    if (!config.base_url || !config.api_key) {
      setMessage({ type: 'error', text: '请填写完整配置后再测试' });
      return;
    }
    if (!isAzure && !config.model_name) {
      setMessage({ type: 'error', text: '请填写完整配置后再测试' });
      return;
    }

    setIsTesting(true);
    setMessage(null);

    const testConfig = isAzure
      ? { ...config, provider: config.provider || 'Azure OpenAI', model_name: config.model_name || 'azure' }
      : config;

    try {
      await onTest(testConfig);
      setMessage({ type: 'success', text: '测试成功！配置正常工作' });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.details || error.message || '测试失败',
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="bg-[#161b22] rounded-xl border border-cyan-900/30 p-6">
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm text-gray-400 mb-6">{description}</p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Provider Type</label>
          <select
            value={config.provider_type || 'openai_compatible'}
            onChange={(e) => handleInputChange('provider_type', e.target.value)}
            className="w-full px-3 py-2 border border-cyan-900/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#0d1117] text-white"
          >
            {PROVIDER_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {!isAzure && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Provider</label>
          <input
            type="text"
            value={config.provider}
            onChange={(e) => handleInputChange('provider', e.target.value)}
            placeholder="例如: OpenAI, Anthropic, DeepSeek"
            className="w-full px-3 py-2 border border-cyan-900/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#0d1117] text-white"
          />
        </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Base URL</label>
          <input
            type="text"
            value={config.base_url}
            onChange={(e) => handleInputChange('base_url', e.target.value)}
            placeholder={
              isAzure
                ? '例如: https://gateway.ai.cloudflare.com/v1/{account_id}/{gateway}/azure-openai/{resource}/gpt-4o/chat/completions?api-version=2024-12-01-preview'
                : '例如: https://api.openai.com/v1'
            }
            className="w-full px-3 py-2 border border-cyan-900/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#0d1117] text-white"
          />
          {isAzure && (
            <p className="mt-1 text-xs text-gray-400">
              Azure via CF AI Gateway 的完整 URL，已包含 /chat/completions 和 api-version
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            {isAzure ? 'CF AI Gateway Token' : 'API Key'}
          </label>
          <div className="relative">
            <input
              type={showApiKey ? 'text' : 'password'}
              value={config.api_key}
              onChange={(e) => handleInputChange('api_key', e.target.value)}
              placeholder={isAzure ? '输入 CF AI Gateway Token' : '输入完整的 API Key'}
              className="w-full px-3 py-2 pr-10 border border-cyan-900/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#0d1117] text-white"
            />
            <button
              type="button"
              onClick={() => setShowApiKey(!showApiKey)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
            >
              {showApiKey ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {!isAzure && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Model Name</label>
          <input
            type="text"
            value={config.model_name}
            onChange={(e) => handleInputChange('model_name', e.target.value)}
            placeholder="例如: gpt-4o, claude-3-5-sonnet-20241022"
            className="w-full px-3 py-2 border border-cyan-900/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-[#0d1117] text-white"
          />
        </div>
        )}
      </div>

      {message && (
        <div
          className={`mt-4 p-3 rounded-md ${
            message.type === 'success' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="mt-6 flex space-x-3">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-4 py-2 rounded-md disabled:bg-gray-700 disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isSaving ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              保存中...
            </>
          ) : (
            '保存配置'
          )}
        </button>

        <button
          onClick={handleTest}
          disabled={isTesting}
          className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isTesting ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              测试中...
            </>
          ) : (
            '测试配置'
          )}
        </button>
      </div>
    </div>
  );
};
